import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './input.css';

const DocumentUpload = ({ title = "Upload Documents", userId, lawyerId, onFileSelect }) => {
  const [files, setFiles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    const invalidFiles = [];

    // Validate files before adding them to state
    selectedFiles.forEach((file) => {
      const validTypes = ['application/pdf', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

      if (validTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      setErrorMessage('Some files are of unsupported type. Only PDF, DOCX, and XLSX are allowed.');
      setShowError(true);
    } else {
      setErrorMessage('');
      setShowError(false);
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);
  };

  const handleDelete = (fileToDelete) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToDelete));
  };

  const uploadDocumentsToServer = async (documents) => {
    setUploading(true);

    const url = 'https://www.quidpro.ai/api/client/uploadAppointmentDocuments';
    const boundary = `----Boundary${uuidv4()}`;

    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('lawyer_id', lawyerId);

    // Append files to the form data
    documents.forEach((document) => {
      formData.append('documents[]', document);
    });

    const options = {
      method: 'POST',
      headers: {
        'Content-Type': `multipart/form-data; boundary=${boundary}`,
      },
      body: formData,
    };

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error('Server error');
      }

      const data = await response.json();
      if (data.documents && data.documents.length > 0) {
        onFileSelect(data.documents); // Pass the list of document URLs to the parent component
      } else {
        setErrorMessage('No documents returned from the server.');
        setShowError(true);
      }
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (files.length === 0) {
      setErrorMessage('Please select at least one document to upload.');
      setShowError(true);
      return;
    }

    uploadDocumentsToServer(files);
  };

  return (
    <div className="uploader-container">
      <label className="uploader-title">{title}</label>
      <div className="upload-box">
        {files.length > 0 ? (
          <div className="file-list">
            {files.map((file, index) => (
              <div key={index} className="file-preview">
                <p className="file-name">{file.name}</p>
                <button
                  className="delete-button"
                  onClick={() => handleDelete(file)}
                  style={{ position: 'absolute', top: 5, right: 5 }}
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        ) : (
          <>
            <input
              type="file"
              accept=".pdf, .xlsx, .xls, .docx, .doc"
              id="file-upload"
              className="file-input"
              onChange={handleFileChange}
              multiple
            />
            <label htmlFor="file-upload" className="upload-label">
              <div className="upload-icon">⬆️</div>
              <p><strong>Click to upload</strong> or drag and drop</p>
              <p className="file-types">PDF, DOCX, XLSX (max. 10MB each)</p>
            </label>
          </>
        )}
      </div>

      <button onClick={handleSubmit} disabled={uploading} className="submit-button">
        {uploading ? 'Uploading...' : 'Upload Documents'}
      </button>

      {showError && (
        <div className="error-popup">
          <p>{errorMessage}</p>
          <button className="close-error" onClick={() => setShowError(false)}>Close</button>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;